import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LanguageContext } from '../../context/LanguageContext';
import { useSiteContent } from '../../context/SiteContentContext';
import { useTranslation } from '../../hooks/useTranslation';

const Navbar = () => {
  const { language, setLanguage, registeredLanguages } = useContext(LanguageContext);
  const { siteContent } = useSiteContent();
  const { t } = useTranslation();
  const location = useLocation();

  // Use translations for nav labels (changes with language)
  // Respect the Admin → CMS → Visibility "Team Page" toggle: when off, the
  // link is removed from both desktop and mobile nav so users can't deep-link
  // into a route that then redirects.
  const teamVisible = siteContent?.teamPage?.visible !== false;
  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/programs', label: t('nav.programs') },
    { path: '/pricing', label: t('nav.pricing') },
    { path: '/about', label: t('nav.about') },
    ...(teamVisible ? [{ path: '/team', label: t('nav.team') }] : []),
    { path: '/contact', label: t('nav.contact') },
  ];

  const isActive = (path) => location.pathname === path;

  // Use header symbol if available, otherwise fall back to full logo
  const logoSrc = siteContent?.images?.headerSymbol || siteContent?.images?.logo || "https://customer-assets.emergentagent.com/job_english-passion/artifacts/94lavxt6_EP%20Logo.jpg";
  const isSymbolLogo = siteContent?.images?.headerSymbol && siteContent.images.headerSymbol.length > 0;

  // Language display codes
  const langDisplayCodes = {
    'en': 'EN', 'ko': 'KR', 'ja': 'JP', 'ru': 'RU', 'it': 'IT',
    'pt': 'PT', 'es': 'ES', 'fr': 'FR', 'zh': 'CN', 'th': 'TH', 'vi': 'VN'
  };

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-slate-100 bg-white/95 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center py-2 sm:py-3" data-testid="navbar-logo">
            <img 
              src={logoSrc} 
              alt="English Passion" 
              className={isSymbolLogo ? "h-8 sm:h-10 w-auto" : "h-10 sm:h-12 md:h-14 w-auto"}
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.path) ? 'text-slate-900' : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3 sm:gap-4">
            <Link
              to="/login"
              className="hidden sm:block text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors"
            >
              {t('nav.login')}
            </Link>
            
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              data-testid="language-select"
              className="text-xs sm:text-sm font-medium text-slate-600 bg-transparent border border-slate-200 rounded-lg px-2 sm:px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-slate-900 cursor-pointer"
            >
              {(registeredLanguages || ['en', 'ko', 'ja']).map(code => (
                <option key={code} value={code}>
                  {langDisplayCodes[code] || code.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Mobile Nav */}
        <div className="md:hidden flex items-center justify-center gap-5 py-2.5 border-t border-slate-100">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-xs font-medium transition-colors ${
                isActive(link.path) ? 'text-slate-900' : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link to="/login" className="text-xs font-medium text-slate-500 hover:text-slate-900">
            {t('nav.login')}
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
